/* Copyright (C) Okahu Inc 2023-2024. All rights reserved.*/

import * as go from 'gojs';

import { LINK_STYLES } from '../../lib/narrative-graph/graph-config';

/**
 * Link styles interface for highlighting
 */
export interface LinkStylesConfig {
  line: {
    stroke: string;
    strokeWidth: number;
  };
  arrow: {
    stroke: string;
    fill: string;
  };
  highlight: {
    fill: string;
    stroke: string;
    strokeWidth: number;
  };
   error: {
    stroke: string;
    strokeWidth: number;
    highlightStroke: string;
  };
  dimmed: {
    stroke: string;
    strokeWidth: number;
    opacity: number;
  };
}

// Default styles (initialized from graph-config, can be overridden via configureLinkStyles)
let linkStyles: LinkStylesConfig = {
  line: {
    stroke: LINK_STYLES.line.stroke,
    strokeWidth: LINK_STYLES.line.strokeWidth,
  },
  arrow: { stroke: LINK_STYLES.arrow.stroke, fill: LINK_STYLES.arrow.fill },
  error: {
    stroke: LINK_STYLES.error.stroke,
    strokeWidth: LINK_STYLES.error.strokeWidth,
    highlightStroke: LINK_STYLES.error.highlightStroke,
  },
  highlight: {
    fill:LINK_STYLES.highlight.stepCircleFill,
    stroke: LINK_STYLES.highlight.stroke,
    strokeWidth: LINK_STYLES.highlight.strokeWidth,
  },
  dimmed: {
    stroke: LINK_STYLES.dimmed.stroke,
    strokeWidth: LINK_STYLES.dimmed.strokeWidth,
    opacity: LINK_STYLES.dimmed.opacity,
  },
};

/**
 * Configure the link styles used for highlighting
 */
export function configureLinkStyles(styles: LinkStylesConfig): void {
  linkStyles = styles;
}

/**
 * Helper to trace path back to turn node and collect all edges and nodes
 */
export function tracePathToTurn(
  startNode: go.Node | null,
  startLink?: go.Link
): { links: go.Link[]; nodes: go.Node[] } {
  const links: go.Link[] = [];
  const nodes: go.Node[] = [];
  const visitedLinks = new Set<string>();
  const visitedNodes = new Set<string>();

  // If starting from a link, add it first
  if (startLink) {
    links.push(startLink);
    visitedLinks.add(startLink.data?.id);
  }

  // Add the start node if provided
  if (startNode && !visitedNodes.has(startNode.key as string)) {
    if (startNode.category !== 'turn') {
      nodes.push(startNode);
    }
    visitedNodes.add(startNode.key as string);
  }

  let currentNode = startLink ? startLink.fromNode : startNode;

  while (currentNode && currentNode.category !== 'turn') {
    // Add current node to path
    if (!visitedNodes.has(currentNode.key as string)) {
      nodes.push(currentNode);
      visitedNodes.add(currentNode.key as string);
    }

    // Find incoming link to this node
    let foundLink: go.Link | null = null;
    currentNode.findLinksInto().each((inLink) => {
      if (!visitedLinks.has(inLink.data?.id)) {
        foundLink = inLink;
      }
    });

    if (foundLink) {
      visitedLinks.add((foundLink as go.Link).data?.id);
      links.push(foundLink as go.Link);
      currentNode = (foundLink as go.Link).fromNode;
    } else {
      break;
    }
  }

  return { links, nodes };
}

/**
 * Highlight the path and dim other edges/nodes
 */
export function highlightPath(
  startNode: go.Node | null,
  startLink: go.Link | null,
  highlight: boolean,
  highlightNodes: boolean = false
): void {
  const diagram = startLink?.diagram || startNode?.diagram;
  if (!diagram) return;

  if (highlight) {
    const path = tracePathToTurn(startNode, startLink || undefined);
    const pathLinkIds = new Set(path.links.map((l) => l.data?.id));
    const pathNodeKeys = new Set(path.nodes.map((n) => n.key as string));

    // Apply styles to all links
    diagram.links.each((l) => {
      // Preserve active/selected link styling
      if (l.isSelected) return;

      const lineShape = l.findObject('LINE') as go.Shape;
      const arrowShape = l.findObject('ARROW') as go.Shape;
      const stepNumberCircle = l.findObject('STEP_CIRCLE') as go.Shape;
      const stepNumberText = l.findObject('STEP_TEXT') as go.TextBlock;

      if (pathLinkIds.has(l.data?.id)) {
        // Highlight path edges  — use error highlight color for error edges
        const isError = l.data?.status === 'ERROR';
        const hoverColor = isError ? linkStyles.error.highlightStroke : linkStyles.highlight.stroke;
        const hoverWidth = isError ? linkStyles.error.strokeWidth : linkStyles.highlight.strokeWidth;
        const circleColor = isError ? linkStyles.error.highlightStroke : linkStyles.highlight.fill;
        if (lineShape) {
          lineShape.stroke = hoverColor;
          lineShape.strokeWidth = hoverWidth;
        }
        if (arrowShape) {
          arrowShape.stroke = circleColor;
          arrowShape.fill = circleColor;
        }
        // Highlight step number circle
        if (stepNumberCircle) {
          stepNumberCircle.fill = circleColor;
          stepNumberCircle.stroke = circleColor;
        }
        // Change text color to dark so it's visible on hover background
        if (stepNumberText) {
          stepNumberText.stroke =isError?'white': LINK_STYLES.highlight.stepTextStroke;
        }
      } else {
        // Dim non-path edges
        if (lineShape) {
          lineShape.stroke = linkStyles.dimmed.stroke;
          lineShape.strokeWidth = linkStyles.dimmed.strokeWidth;
          lineShape.opacity = linkStyles.dimmed.opacity;
        }
        if (arrowShape) {
          arrowShape.stroke = linkStyles.dimmed.stroke;
          arrowShape.fill = linkStyles.dimmed.stroke;
          arrowShape.opacity = linkStyles.dimmed.opacity;
        }
        // Dim step number circle
        if (stepNumberCircle) {
          stepNumberCircle.fill = linkStyles.dimmed.stroke;
          stepNumberCircle.stroke = linkStyles.dimmed.stroke;
          stepNumberCircle.opacity = linkStyles.dimmed.opacity;
        }
        // Dim step number text
        if (stepNumberText) {
          stepNumberText.opacity = linkStyles.dimmed.opacity;
        }
      }
    });

    // Apply styles to nodes only if highlightNodes is true
    if (highlightNodes) {
      diagram.nodes.each((n) => {
        if (n.category === 'turn') return; // Don't dim turn nodes

        const shape = n.findMainElement() as go.Shape;
        if (pathNodeKeys.has(n.key as string)) {
          // Highlight path nodes
          if (shape) shape.opacity = 1;
          n.opacity = 1;
        } else {
          // Dim non-path nodes
          if (shape) shape.opacity = 0.3;
          n.opacity = 0.3;
        }
      });
    }
  } else {
    // Collect link IDs that belong to any selected node's active path
    const activePathLinkIds = new Set<string>();
    diagram.nodes.each((n) => {
      if (n.isSelected && n.category !== 'turn') {
        const activePath = tracePathToTurn(n);
        activePath.links.forEach((al) => {
          if (al.data?.id) activePathLinkIds.add(al.data.id);
        });
      }
    });

    // Reset all edges to normal (but preserve selection and active path state)
    diagram.links.each((l) => {
      const lineShape = l.findObject('LINE') as go.Shape;
      const arrowShape = l.findObject('ARROW') as go.Shape;
      const stepNumberCircle = l.findObject('STEP_CIRCLE') as go.Shape;
      const stepNumberText = l.findObject('STEP_TEXT') as go.TextBlock;

      // Check if link is selected or part of a selected node's active path
      if (l.isSelected || activePathLinkIds.has(l.data?.id)) {
        // Active/Selected state colors — use error highlight for error edges
         const isError = l.data?.status === 'ERROR';
        const activeColor = isError ? linkStyles.error.highlightStroke : LINK_STYLES.selected.stroke;
        const circleColor = isError ? linkStyles.error.highlightStroke : LINK_STYLES.selected.stepCircleStroke;
        if (lineShape) {
          lineShape.stroke = activeColor;
          lineShape.strokeWidth = linkStyles.line.strokeWidth;
          lineShape.opacity = 1;
        }
        if (arrowShape) {
          arrowShape.stroke = activeColor;
          arrowShape.fill = activeColor;
          arrowShape.opacity = 1;
        }
        if (stepNumberCircle) {
          stepNumberCircle.fill = circleColor;
          stepNumberCircle.stroke = circleColor;
          stepNumberCircle.opacity = 1;
        }
        if (stepNumberText) {
          stepNumberText.stroke = LINK_STYLES.selected.stepTextStroke;
          stepNumberText.opacity = 1;
        }
      } else {
        // Normal state colors — respect error status
        const isError = l.data?.status === 'ERROR';
        if (lineShape) {
           lineShape.stroke = isError ? linkStyles.error.stroke : linkStyles.line.stroke;
          lineShape.strokeWidth = isError ? linkStyles.error.strokeWidth : linkStyles.line.strokeWidth;
          lineShape.opacity = 1;
        }
        if (arrowShape) {
          arrowShape.stroke = isError ? linkStyles.error.stroke : linkStyles.arrow.stroke;
          arrowShape.fill = isError ? linkStyles.error.stroke : linkStyles.arrow.fill;
          arrowShape.opacity = 1;
        }
        // Reset step number circle to default — respect error status
        if (stepNumberCircle) {
          stepNumberCircle.fill = LINK_STYLES.stepNumber.circleFill;
          stepNumberCircle.stroke = isError ? LINK_STYLES.error.stroke : LINK_STYLES.stepNumber.circleStroke;
          stepNumberCircle.opacity = 1;
        }
        // Reset step number text to default
        if (stepNumberText) {
          stepNumberText.stroke = LINK_STYLES.stepNumber.textStroke;
          stepNumberText.opacity = 1;
        }
      }
    });

    // Reset all nodes to normal (only if highlightNodes was used)
    if (highlightNodes) {
      diagram.nodes.each((n) => {
        const shape = n.findMainElement() as go.Shape;
        if (shape) shape.opacity = 1;
        n.opacity = 1;
      });
    }
  }
}

/**
 * Apply active (selected) styling to the path from a node back to the turn node
 */
export function highlightPathActive(startNode: go.Node): void {
  const path = tracePathToTurn(startNode);

  path.links.forEach((l) => {
    const lineShape = l.findObject('LINE') as go.Shape;
    const arrowShape = l.findObject('ARROW') as go.Shape;
    const stepNumberCircle = l.findObject('STEP_CIRCLE') as go.Shape;
    const stepNumberText = l.findObject('STEP_TEXT') as go.TextBlock;

    // Use error highlight color for error edges
    const isError = l.data?.status === 'ERROR';
    const activeColor = isError ? linkStyles.error.highlightStroke : LINK_STYLES.selected.stroke;
    const circleColor = isError ? linkStyles.error.highlightStroke : LINK_STYLES.selected.stepCircleStroke;

    if (lineShape) {
      lineShape.stroke = activeColor;
      lineShape.strokeWidth = linkStyles.line.strokeWidth;
      lineShape.opacity = 1;
    }
    if (arrowShape) {
      arrowShape.stroke = activeColor;
      arrowShape.fill = activeColor;
      arrowShape.opacity = 1;
    }
    if (stepNumberCircle) {
      stepNumberCircle.fill = circleColor;
      stepNumberCircle.stroke = circleColor;
      stepNumberCircle.opacity = 1;
    }
    if (stepNumberText) {
      stepNumberText.stroke = LINK_STYLES.selected.stepTextStroke;
      stepNumberText.opacity = 1;
    }
  });
}

/**
 * Reset path links from a node back to default styling
 */
export function resetPathLinks(startNode: go.Node): void {
  const path = tracePathToTurn(startNode);

  path.links.forEach((l) => {
    // Don't reset if the link is still selected independently
    if (l.isSelected) return;

    const lineShape = l.findObject('LINE') as go.Shape;
    const arrowShape = l.findObject('ARROW') as go.Shape;
    const stepNumberCircle = l.findObject('STEP_CIRCLE') as go.Shape;
    const stepNumberText = l.findObject('STEP_TEXT') as go.TextBlock;

    // Respect error status when resetting to normal
    const isError = l.data?.status === 'ERROR';

    if (lineShape) {
      lineShape.stroke = isError ? linkStyles.error.stroke : linkStyles.line.stroke;
      lineShape.strokeWidth = isError ? linkStyles.error.strokeWidth : linkStyles.line.strokeWidth;
      lineShape.opacity = 1;
    }
    if (arrowShape) {
      arrowShape.stroke = isError ? linkStyles.error.stroke : linkStyles.arrow.stroke;
      arrowShape.fill = isError ? linkStyles.error.stroke : linkStyles.arrow.fill;
      arrowShape.opacity = 1;
    }
    if (stepNumberCircle) {
      stepNumberCircle.fill = LINK_STYLES.stepNumber.circleFill;
      stepNumberCircle.stroke = isError ? LINK_STYLES.error.stroke : LINK_STYLES.stepNumber.circleStroke;
      stepNumberCircle.opacity = 1;
    }
    if (stepNumberText) {
      stepNumberText.stroke = LINK_STYLES.stepNumber.textStroke;
      stepNumberText.opacity = 1;
    }
  });
}
